/**
 * Vista 9 · Mis oficios — bandeja del notificador.
 *
 * **Diseño móvil primero.** Es la única vista cuyo contexto de uso principal
 * es un teléfono en la calle, así que la unidad es una tarjeta a ancho
 * completo y la acción está donde llega el pulgar, no una tabla de seis
 * columnas que obliga a desplazarse en horizontal.
 *
 * `ARQ-11`: el notificador ve oficio, entidad y dirección. **Nada del
 * trabajador ni de la fiscalización.** No le hace falta para su trabajo, y
 * dárselo sería repartir datos personales sin motivo.
 *
 * Una acción por paso EXACTO: el paso 4 es «recibí el oficio» y el 5 es «lo
 * entregué». Ofrecer ambas a la vez es lo que produce `PASO_DUPLICADO`.
 */
import { useState } from 'react'
import { Button, Card, CardContent, CardHeader, CardTitle, StatusBadge } from '@mpfn/ui'
import { EmptyState, PageHeader } from '@mpfn/patterns'
import { Download, MapPin, PackageCheck } from 'lucide-react'
import { useOficiosAsignados } from '../api/consultas.js'
import { canalDe, pasoDe, useCatalogos } from '../api/catalogos.js'
import { urlDeOficio } from '../api/cliente.js'
import type { OficioEnCircuito } from '../api/tipos.js'
import { Datos } from '../componentes/Datos.js'
import { Pagina } from '../componentes/Pagina.js'
import { DialogoPaso } from '../componentes/DialogoPaso.js'
import { fecha } from '../lib/formato.js'

/** Los pasos del canal físico que ejecuta el notificador. */
const PASOS_DEL_NOTIFICADOR = [4, 5, 6] as const

export function MisOficios() {
  const oficios = useOficiosAsignados()

  return (
    <Pagina className="sigfisc-vista--movil">
      <Datos estado={oficios}>
        {(lista) => (
          <>
            <PageHeader
              title="Mis oficios"
              description={`${String(lista.length)} asignados`}
            />
            {lista.length === 0 ? (
              <EmptyState
                icon={PackageCheck}
                title="No tiene oficios asignados"
                description="Mesa de Partes le asignará los oficios que deba entregar."
              />
            ) : (
              <div className="sigfisc-oficios">
                {lista.map((oficio) => (
                  <TarjetaOficio key={oficio.consultaId} oficio={oficio} />
                ))}
              </div>
            )}
          </>
        )}
      </Datos>
    </Pagina>
  )
}

function TarjetaOficio({ oficio }: { oficio: OficioEnCircuito }) {
  const catalogos = useCatalogos()
  const [registrando, setRegistrando] = useState(false)

  const definicion = canalDe(catalogos, 'fisico')
  const siguiente = oficio.ultimoPaso + 1
  const leToca = (PASOS_DEL_NOTIFICADOR as readonly number[]).includes(siguiente)
  const descripcion = pasoDe(catalogos, 'fisico', oficio.ultimoPaso)?.descripcion

  return (
    <Card className="sigfisc-oficio">
      <CardHeader className="sigfisc-oficio__cabecera">
        <CardTitle>Oficio {oficio.numeroOficio}</CardTitle>
        <StatusBadge
          tone={siguiente === 5 ? 'warning' : 'info'}
          label={`${String(oficio.ultimoPaso)}/${String(definicion?.totalPasos ?? 9)}`}
        />
      </CardHeader>
      <CardContent className="sigfisc-oficio__cuerpo">
        <p className="sigfisc-oficio__entidad">{oficio.entidad}</p>
        <p className="sigfisc-oficio__destino">
          <MapPin size={14} aria-hidden="true" />
          {oficio.destino}
        </p>
        <p className="sigfisc-nota">
          {descripcion ?? 'En circulación'} · {fecha(oficio.fechaApertura)}
        </p>

        <div className="sigfisc-oficio__acciones">
          {oficio.documentoOficioId !== undefined && (
            <Button variant="secondary" size="sm" asChild>
              <a href={urlDeOficio(oficio.consultaId)} download>
                <Download size={14} aria-hidden="true" />
                Ver oficio
              </a>
            </Button>
          )}

          {leToca && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                setRegistrando(true)
              }}
            >
              {siguiente === 4 ? 'Recibí el oficio' : siguiente === 5 ? 'Entregué el oficio' : 'Informar la entrega'}
            </Button>
          )}
        </div>
      </CardContent>

      {leToca && (
        <DialogoPaso
          consultaId={oficio.consultaId}
          canal="fisico"
          paso={siguiente}
          abierto={registrando}
          onCambio={setRegistrando}
        />
      )}
    </Card>
  )
}
